/**
 * @module frest
 */
import * as tslib_1 from "tslib";

var e_1, _a, e_2, _b;

import qs from 'querystringify';
import { FrestError } from './FrestError';
import xhr from './xhr';
import * as utils from './utils';
import { InterceptorManager } from './InterceptorManager';
var methodsNoData = ['get', 'delete', 'options', 'download'];
var methodsData = ['post', 'put', 'patch', 'upload'];

var setCt = function setCt(headers, value) {
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', value);
  }
};

var json = function json(raw) {
  var ct = raw.headers.get('Content-Type');

  if (ct && ct.indexOf('application/json') >= 0) {
    return raw.clone().json().catch(function (_) {
      return raw.body;
    });
  }

  return raw.body;
};

var req = function req(_a, data) {
  var headers = _a.headers;

  if (utils.isFormData(data) || utils.isArrayBuffer(data) || utils.isBuffer(data) || utils.isStream(data) || utils.isFile(data) || utils.isBlob(data)) {
    return data;
  }

  if (utils.isArrayBufferView(data)) {
    return data.buffer;
  }

  if (utils.isURLSearchParams(data)) {
    setCt(headers, 'application/x-www-form-urlencoded;charset=utf-8');
    return data.toString();
  }

  if (utils.isObject(data)) {
    setCt(headers, 'application/json;charset=utf-8');
    return JSON.stringify(data);
  }

  return data;
};

var resp = function resp(raw, // tslint:disable-next-line:no-object-literal-type-assertion
data) {
  if (data === void 0) {
    data = {};
  }

  return {
    raw: raw,
    data: data,
    headers: raw.headers,
    ok: raw.ok,
    redirected: raw.redirected,
    status: raw.status,
    statusText: raw.statusText,
    trailer: raw.trailer,
    type: raw.type,
    url: raw.url
  };
};

var checkInt = function checkInt(ret, type) {
  if (!ret) {
    var w = type === 'request' ? type + " config" : type;
    throw new Error("one of interceptor didn't return " + w);
  }
};
/**
 * Default configuration if Frest instance is created without any configuration.
 * @public
 */


export var DEFAULT_CONFIG = {
  base: '',
  fetch: fetch,
  headers: {
    common: new Headers({
      Accept: 'application/json, text/plain, */*'
    }),
    post: new Headers(),
    get: new Headers(),
    put: new Headers(),
    delete: new Headers(),
    patch: new Headers(),
    options: new Headers()
  },
  method: 'GET',
  transformResponse: [json],
  transformRequest: [req]
};
/**
 * The main Frest class.
 * @public
 */

var Frest =
/** @class */
function () {
  /**
   * Creates an instance of Frest.
   * @param config - Configuration for this instance.
   * Can be string or array of string (in which it'll be the `base` URL for
   * every requests), or a {@link IConfig} object. Defaults to `DEFAULT_CONFIG`
   */
  function Frest(config) {
    var _this = this;

    this.interceptors = {
      request: new InterceptorManager(),
      response: new InterceptorManager(),
      error: new InterceptorManager()
    };

    this.req = function (request) {
      var fetchFn;

      try {
        fetchFn = _this.getFetch(request);
      } catch (error) {
        return Promise.reject(error);
      }

      var fullPath = _this.parsePath(request.path, request.query);

      return fetchFn(fullPath, request).then(function (raw) {
        return {
          request: request,
          raw: raw
        };
      });
    };

    this.after = function (afterFetch) {
      var e_3, _a, e_4, _b;

      var raw = afterFetch.raw,
          request = afterFetch.request;
      var dataPromise = Promise.resolve({});

      var _loop_3 = function _loop_3(transform) {
        dataPromise = dataPromise.then(function (data) {
          return transform(raw, data);
        });
      };

      try {
        for (var _c = tslib_1.__values(request.transformResponse), _d = _c.next(); !_d.done; _d = _c.next()) {
          var transform = _d.value;

          _loop_3(transform);
        }
      } catch (e_3_1) {
        e_3 = {
          error: e_3_1
        };
      } finally {
        try {
          if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        } finally {
          if (e_3) throw e_3.error;
        }
      }

      if (!raw.ok) {
        return dataPromise.then(function (data) {
          return Promise.reject(new FrestError("Non OK HTTP response status: " + raw.status + " - " + raw.statusText, _this, request, resp(raw, data)));
        });
      }

      var responsePromise = dataPromise.then(function (data) {
        return resp(raw, data);
      });

      var _loop_4 = function _loop_4(responseInterceptor) {
        responsePromise = responsePromise.then(function (response) {
          checkInt(response, 'response');
          return responseInterceptor({
            frest: _this,
            request: request,
            response: response
          });
        });
      };

      try {
        for (var _e = tslib_1.__values(_this.interceptors.response.handlers), _f = _e.next(); !_f.done; _f = _e.next()) {
          var responseInterceptor = _f.value;

          _loop_4(responseInterceptor);
        }
      } catch (e_4_1) {
        e_4 = {
          error: e_4_1
        };
      } finally {
        try {
          if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
        } finally {
          if (e_4) throw e_4.error;
        }
      }

      return responsePromise.then(function (r) {
        checkInt(r, 'response');
        return r;
      }).catch(function (e) {
        var cause = typeof e === 'string' ? e : e.message ? e.message : e;
        return Promise.reject(new FrestError("Error in response transform/interceptor: " + cause, _this, request, resp(raw)));
      });
    };

    this.onError = function (request) {
      return function (e) {
        var err = _this.toFrestError(e, request);

        if (_this.interceptors.error.handlers.length === 0) {
          return Promise.reject(err);
        }

        return new Promise(function (resolve, reject) {
          var e_5, _a;

          var promise = Promise.resolve(null);
          var recovery = null;

          var _loop_5 = function _loop_5(errorInterceptor) {
            if (recovery != null) {
              return "break";
            }

            promise = promise.then(function (rec) {
              if (rec != null) {
                recovery = rec;
                return rec;
              }

              return errorInterceptor(err);
            }).catch(function (ee) {
              err = _this.toFrestError(ee, request);
              return null;
            });
          };

          try {
            for (var _b = tslib_1.__values(_this.interceptors.error.handlers), _c = _b.next(); !_c.done; _c = _b.next()) {
              var errorInterceptor = _c.value;

              var state_1 = _loop_5(errorInterceptor);

              if (state_1 === "break") break;
            }
          } catch (e_5_1) {
            e_5 = {
              error: e_5_1
            };
          } finally {
            try {
              if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            } finally {
              if (e_5) throw e_5.error;
            }
          }

          promise.then(function (res) {
            if (res) {
              resolve(res);
            } else {
              reject(err);
            }
          });
        });
      };
    };

    if (config && typeof config === 'string') {
      this.config = tslib_1.__assign({}, DEFAULT_CONFIG, {
        base: config
      });
    } else if (config && typeof config === 'object') {
      var headers = tslib_1.__assign({}, DEFAULT_CONFIG.headers, config.headers);

      this.config = tslib_1.__assign({}, DEFAULT_CONFIG, config, {
        headers: headers
      });
    } else {
      this.config = tslib_1.__assign({}, DEFAULT_CONFIG);
    }

    this.config.base = utils.trimSlashes(this.config.base);
  }

  Object.defineProperty(Frest.prototype, "base", {
    /**
     * Get base URL used in this instance.
     */
    get: function get() {
      return this.config.base;
    },
    enumerable: true,
    configurable: true
  });

  Frest.prototype.create = function (config) {
    return new Frest(config);
  };
  /**
   * Merge this instance config with the one provided here.
   * @param config - Configuration to be merged into this instance's configuration.
   */


  Frest.prototype.mergeConfig = function (config) {
    var headers = tslib_1.__assign({}, this.config.headers, config.headers);

    this.config = tslib_1.__assign({}, this.config, config, {
      headers: headers
    });
  };

  Object.defineProperty(Frest.prototype, "fetchFn", {
    /**
     * Get `fetch` function used in this instance.
     * @remarks
     * This can be the native `fetch` API or any function with similar signature.
     */
    get: function get() {
      return this.config.fetch;
    },
    enumerable: true,
    configurable: true
  });
  /**
   * Get full URL from the provided path and query object/string.
   * @remarks
   * This will use the instance's `base` URL configuration and construct full
   * URL to the provided arguments.
   *
   * @param path - Endpoint path
   * @param query - query object/string to include
   * @returns Full URL to the provided arguments.
   */

  Frest.prototype.parsePath = function (path, query) {
    var paths = path ? path instanceof Array ? path : [path] : [''];
    query = this.parseQuery(query);
    return utils.trimSlashes(this.config.base + "/" + paths.map(encodeURI).join('/') + query);
  };
  /**
   * Utility function to parse a query object into string
   *
   * @param query - The query to parse. It can be object/string
   * @returns Parsed query string
   */


  Frest.prototype.parseQuery = function (query) {
    var q = query || '';

    if (typeof q === 'object') {
      q = qs.stringify(q, '?');
    } else if (q !== '') {
      q = q.charAt(0) === '?' ? q : "?" + q;
    }

    return q;
  };
  /**
   * Make a request to an endpoint.
   *
   * @template T - The type of response's body, if any. Defaults to `any`.
   * @param init - A string, string array, or request configuration object.
   * @param request - request configuration if the first arg is string
   * or string array
   * @returns Response promise which will be resolved when the request is successful.
   * The promise will throws in case of error in any request life-cycle.
   */


  Frest.prototype.request = function (init, request) {
    if (request === void 0) {
      request = {};
    }

    var conf = tslib_1.__assign({
      action: 'request',
      method: this.config.method
    }, this.requestConfig(init, request));

    return this.internalRequest(tslib_1.__assign({}, conf, {
      headers: this.headers(conf)
    }));
  };

  Frest.prototype.internalRequest = function (request) {
    return this.before(request).then(this.req).then(this.after).catch(this.onError(request));
  };

  Frest.prototype.requestConfig = function (init, request) {
    var _a = this.config,
        fetch = _a.fetch,
        base = _a.base,
        method = _a.method,
        headers = _a.headers,
        rest = tslib_1.__rest(_a, ["fetch", "base", "method", "headers"]);

    if (typeof init === 'string' || init instanceof Array) {
      return tslib_1.__assign({
        path: init
      }, rest, request);
    }

    return tslib_1.__assign({
      path: ''
    }, rest, init);
  };

  Frest.prototype.headers = function (request) {
    var e_6, _a, e_7, _b;

    var method = request.method.toLowerCase();
    var headers = new Headers(this.config.headers.common);

    try {
      for (var _c = tslib_1.__values(this.config.headers[method].entries()), _d = _c.next(); !_d.done; _d = _c.next()) {
        var header = _d.value;
        headers.set(header[0], header[1]);
      }
    } catch (e_6_1) {
      e_6 = {
        error: e_6_1
      };
    } finally {
      try {
        if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
      } finally {
        if (e_6) throw e_6.error;
      }
    }

    if (request.headers) {
      try {
        for (var _e = tslib_1.__values(request.headers.entries()), _f = _e.next(); !_f.done; _f = _e.next()) {
          var header = _f.value;
          headers.set(header[0], header[1]);
        }
      } catch (e_7_1) {
        e_7 = {
          error: e_7_1
        };
      } finally {
        try {
          if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
        } finally {
          if (e_7) throw e_7.error;
        }
      }
    }

    return headers;
  };

  Frest.prototype.getFetch = function (request) {
    if (request.action === 'upload' || request.action === 'download') {
      return xhr;
    }

    if (typeof request.fetch === 'function') {
      return request.fetch;
    } else if (typeof this.config.fetch === 'function') {
      return this.config.fetch;
    }

    throw new FrestError('Fetch API is not available in this browser', this, request);
  };

  Frest.prototype.before = function (request) {
    var _this = this;

    return new Promise(function (resolve, reject) {
      var e_8, _a, e_9, _b;

      var dataPromise = Promise.resolve(request.body);

      var _loop_6 = function _loop_6(transform) {
        if (methodsData.indexOf(request.method.toLowerCase()) >= 0) {
          dataPromise = dataPromise.then(function (data) {
            return transform(request, data);
          });
        }
      };

      try {
        for (var _c = tslib_1.__values(request.transformRequest), _d = _c.next(); !_d.done; _d = _c.next()) {
          var transform = _d.value;

          _loop_6(transform);
        }
      } catch (e_8_1) {
        e_8 = {
          error: e_8_1
        };
      } finally {
        try {
          if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        } finally {
          if (e_8) throw e_8.error;
        }
      }

      var requestPromise = dataPromise.then(function (body) {
        request.body = body;
        return request;
      });

      var _loop_7 = function _loop_7(requestInterceptor) {
        requestPromise = requestPromise.then(function (requestConfig) {
          checkInt(requestConfig, 'request');
          return requestInterceptor({
            frest: _this,
            request: requestConfig
          });
        });
      };

      try {
        for (var _e = tslib_1.__values(_this.interceptors.request.handlers), _f = _e.next(); !_f.done; _f = _e.next()) {
          var requestInterceptor = _f.value;

          _loop_7(requestInterceptor);
        }
      } catch (e_9_1) {
        e_9 = {
          error: e_9_1
        };
      } finally {
        try {
          if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
        } finally {
          if (e_9) throw e_9.error;
        }
      }

      requestPromise.then(function (requestConfig) {
        checkInt(requestConfig, 'request');
        resolve(requestConfig);
      }).catch(function (e) {
        var cause = typeof e === 'string' ? e : e.message ? e.message : e;
        reject(new FrestError("Error in request transform/interceptor: " + cause, _this, request));
      });
    });
  };

  Frest.prototype.toFrestError = function (e, requestConfig) {
    return utils.isFrestError(e) ? new FrestError(e.message, this, requestConfig, e.response) : e;
  };

  return Frest;
}();

var _loop_1 = function _loop_1(method) {
  var meth = method === 'download' ? 'GET' : method.toUpperCase();

  Frest.prototype[method] = function (init, request) {
    if (request === void 0) {
      request = {};
    }

    var conf = tslib_1.__assign({
      action: method,
      method: meth
    }, this.requestConfig(init, request));

    return this.internalRequest(tslib_1.__assign({}, conf, {
      headers: this.headers(conf)
    }));
  };
};

try {
  for (var methodsNoData_1 = tslib_1.__values(methodsNoData), methodsNoData_1_1 = methodsNoData_1.next(); !methodsNoData_1_1.done; methodsNoData_1_1 = methodsNoData_1.next()) {
    var method = methodsNoData_1_1.value;

    _loop_1(method);
  }
} catch (e_1_1) {
  e_1 = {
    error: e_1_1
  };
} finally {
  try {
    if (methodsNoData_1_1 && !methodsNoData_1_1.done && (_a = methodsNoData_1.return)) _a.call(methodsNoData_1);
  } finally {
    if (e_1) throw e_1.error;
  }
}

var _loop_2 = function _loop_2(method) {
  var meth = method === 'upload' ? 'POST' : method.toUpperCase();

  Frest.prototype[method] = function (init, body, request) {
    if (request === void 0) {
      request = {};
    }

    var conf = tslib_1.__assign({
      action: method,
      method: meth
    }, this.requestConfig(init, request));

    return this.internalRequest(tslib_1.__assign({
      body: body
    }, conf, {
      headers: this.headers(conf)
    }));
  };
};

try {
  for (var methodsData_1 = tslib_1.__values(methodsData), methodsData_1_1 = methodsData_1.next(); !methodsData_1_1.done; methodsData_1_1 = methodsData_1.next()) {
    var method = methodsData_1_1.value;

    _loop_2(method);
  }
} catch (e_2_1) {
  e_2 = {
    error: e_2_1
  };
} finally {
  try {
    if (methodsData_1_1 && !methodsData_1_1.done && (_b = methodsData_1.return)) _b.call(methodsData_1);
  } finally {
    if (e_2) throw e_2.error;
  }
}

export { Frest };